import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const DeleteTemplateVariablesSchema = z.object({
  id: z.string(),
});

export type DeleteTemplateVariables = z.infer<typeof DeleteTemplateVariablesSchema>;

async function deleteTemplateFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: DeleteTemplateVariables,
) {
  const parsed = DeleteTemplateVariablesSchema.parse(variables);
  const instance = await axiosInstance;
  return instance.delete(`/templates/${parsed.id}`);
}

export function useDeleteTemplate() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DeleteTemplateVariables>({
    mutationFn: variables => deleteTemplateFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    DeleteTemplateVariables,
    'deleteTemplate'
  >('deleteTemplate', mutation);
}
